// おまもりガチャ（GAME_DESIGN 18章・19章。ガチャ券1枚で1回。券は遊んで手に入るだけで、円では買えない。霊玉は使わない）。
// 1回ごとに抽選ランク（S・A・B・C・D）を引き、景品を引く。景品はお守り（ランクでレア度の抽選が変わる）・式神の札・材木・銭・主EXP・装備・名物（大当たり）。
// ランクの内部キー（daikichi〜kyo）・保存データの p.omikuji・API の fortune は互換のため旧名のまま。画面に出す名前は FORTUNE_LABEL。
// - 10連はランクA以上を1つ確定（無ければ最後の1回のランクを S・A で引き直す）
// - 天井：極のお守りが出ないまま CONFIG.gacha.pity 回（券・無料の合計）で極が確定
// - 週替わりのピックアップ：JST の週の日付シードで主効果1種の出現率アップ
// - 毎日1回の無料ガチャ（券不要、JST の日付）：そのランクでその日だけの小さなボーナス（S＝ドロップ+20% など）。D は翌日の無料が S・A になりやすい
import type { RngHolder } from '../rng';
import { randInt, seedOf, weighted } from '../rng';
import { MEIBUTSU } from '../data/equip-lines';
import { CONFIG } from './config';
import { addItem, EQUIPMENT, rollAffixes, rollEquipDrop, type EquipDrop } from './equipment';
import { gainExp } from './level';
import { addOmamori, DROP_OMAMORI_IDS, OMAMORI, RARITY_LABEL, rollOmamori, type OmamoriRoll } from './omamori';
import { chapterOf, highestCleared, RARITIES, type Fortune, type OpResult, type Profile, type Rarity, type Resources } from './profile';

export const FORTUNES: readonly Fortune[] = ['daikichi', 'chukichi', 'kichi', 'suekichi', 'kyo'];
export const FORTUNE_LABEL: Record<Fortune, string> = { daikichi: 'ランクS', chukichi: 'ランクA', kichi: 'ランクB', suekichi: 'ランクC', kyo: 'ランクD' };

export type GachaPrize = 'omamori' | 'equipment' | 'meibutsu' | 'fuda' | 'zaimoku' | 'exp' | 'zeni';
const PRIZES: GachaPrize[] = ['omamori', 'fuda', 'zaimoku', 'zeni', 'exp', 'equipment', 'meibutsu'];

export type GachaItem =
  | { kind: 'omamori'; fortune: Fortune; defId: string; omamori: OmamoriRoll; pickup?: boolean; itemId?: number | null; pity?: boolean; zeni?: number }
  | { kind: 'equipment'; fortune: Fortune; drop: EquipDrop; meibutsu?: boolean; itemId?: number | null }
  | { kind: 'fuda' | 'zaimoku' | 'exp' | 'zeni'; fortune: Fortune; amount: number };

export const GACHA = {
  get cost1() {
    return CONFIG.gacha.cost1;
  },
  get cost10() {
    return CONFIG.gacha.cost10;
  },
  get pity() {
    return CONFIG.gacha.pity;
  },
};

export const gachaCost = (count: number) => (count === 10 ? CONFIG.gacha.cost10 : CONFIG.gacha.cost1 * count);

// ---------- 日付（JST の 'YYYY-MM-DD'） ----------

const parseDay = (day: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : NaN;
};
const fmtDay = (t: number) => new Date(t).toISOString().slice(0, 10);
/** 翌日 */
export const nextDay = (day: string) => {
  const t = parseDay(day);
  return Number.isFinite(t) ? fmtDay(t + 86400000) : '';
};
/** その日を含む週の月曜 */
export const weekOfDay = (day: string) => {
  const t = parseDay(day);
  if (!Number.isFinite(t)) return '';
  const dow = (new Date(t).getUTCDay() + 6) % 7;
  return fmtDay(t - dow * 86400000);
};

/** 今週のピックアップ（主効果1種）。週の日付シードで自動。until はその週の最後の日 */
export function omikujiPickup(dayKey: string): { defId: string; name: string; week: string; until: string } {
  const week = weekOfDay(dayKey) || dayKey;
  const defId = DROP_OMAMORI_IDS[seedOf(`pickup:${week}`) % DROP_OMAMORI_IDS.length];
  const until = Number.isFinite(parseDay(week)) ? fmtDay(parseDay(week) + 6 * 86400000) : '';
  return { defId, name: OMAMORI[defId].name, week, until };
}

const pickupKinds = (dayKey: string) => {
  const pk = omikujiPickup(dayKey).defId;
  return DROP_OMAMORI_IDS.map((id) => ({ item: id, weight: id === pk ? CONFIG.gacha.pickupMult : 1 }));
};

// ---------- 排出率（表示用） ----------

/** luck：ランクS・A の重みの倍率（D の翌日の無料は kyoNextDayMult、A の日のガチャ（券・ショップの10連・月パスの10連）は perks.chukichi.luck） */
function fortuneWeights(luck = 1) {
  const g = CONFIG.gacha;
  return FORTUNES.map((f) => ({ item: f, weight: (g.fortuneWeights[f] ?? 0) * (g.tenGuarantee.includes(f) ? luck : 1) }));
}

const PRIZE_LABEL = (p: GachaPrize) => {
  const g = CONFIG.gacha;
  switch (p) {
    case 'omamori':
      return 'お守り';
    case 'fuda':
      return '式神の札 ×1';
    case 'zaimoku':
      return `材木 ×${g.zaimokuAmount}`;
    case 'zeni':
      return `銭 ×${g.zeniAmount}`;
    case 'exp':
      return `主EXP ${g.expAmount}`;
    case 'equipment':
      return '装備（良・優の銘つき）';
    case 'meibutsu':
      return '名物の装備（極・大当たり）';
  }
};

/** 排出率（合計1）。ランク・景品・お守りのレア度（全体）・ランクごとのレア度・主効果（ピックアップ込み） */
export function omikujiRates(dayKey = '') {
  const g = CONFIG.gacha;
  const fw = fortuneWeights();
  const fTotal = fw.reduce((a, x) => a + x.weight, 0);
  const fortunes = fw.map((x) => ({ fortune: x.item, label: FORTUNE_LABEL[x.item], rate: x.weight / fTotal }));
  const pTotal = PRIZES.reduce((a, k) => a + (g.rates[k] ?? 0), 0);
  const prizes = PRIZES.map((k) => ({ prize: k, label: PRIZE_LABEL(k), rate: (g.rates[k] ?? 0) / pTotal }));
  const byFortune = Object.fromEntries(
    FORTUNES.map((f) => {
      const w = g.rarityByFortune[f];
      const t = RARITIES.reduce((a, r) => a + (w[r] ?? 0), 0) || 1;
      return [f, Object.fromEntries(RARITIES.map((r) => [r, (w[r] ?? 0) / t]))];
    }),
  ) as Record<Fortune, Record<Rarity, number>>;
  const rarity = Object.fromEntries(RARITIES.map((r) => [r, fortunes.reduce((a, f) => a + f.rate * byFortune[f.fortune][r], 0)])) as Record<Rarity, number>;
  const kinds = dayKey ? pickupKinds(dayKey) : DROP_OMAMORI_IDS.map((id) => ({ item: id, weight: 1 }));
  const kTotal = kinds.reduce((a, x) => a + x.weight, 0);
  const omamori = kinds.map((k) => ({ defId: k.item, name: OMAMORI[k.item].name, rate: k.weight / kTotal, pickup: k.weight > 1 }));
  return { fortunes, prizes, rarity, byFortune, omamori, pickup: dayKey ? omikujiPickup(dayKey) : null, pity: g.pity, tenGuarantee: g.tenGuarantee.map((f) => FORTUNE_LABEL[f as Fortune]) };
}

/** 互換：表示用の排出率表（合計1）。景品とお守りのレア度 */
export function gachaRates(dayKey = ''): { label: string; rate: number }[] {
  const r = omikujiRates(dayKey);
  const om = r.prizes.find((x) => x.prize === 'omamori')!.rate;
  return [
    ...RARITIES.map((x) => ({ label: `お守り（${RARITY_LABEL[x]}）`, rate: om * r.rarity[x] })),
    ...r.prizes.filter((x) => x.prize !== 'omamori').map((x) => ({ label: x.label, rate: x.rate })),
  ];
}

// ---------- 抽選 ----------

export interface RollGachaOpts {
  dayKey?: string;
  /** 無料の1回（D の翌日なら S・A が出やすい） */
  lucky?: boolean;
  /** ランクS・A の重みの倍率（lucky より優先） */
  luck?: number;
}

/** 抽選のみ（profile・資源は変えない）。pityAfter を返す */
export function rollGacha(h: RngHolder, count: number, profile: Profile, o: RollGachaOpts = {}): { items: GachaItem[]; pityAfter: number; cost: number } {
  const g = CONFIG.gacha;
  const tier = Math.max(1, chapterOf(Math.max(1, highestCleared(profile))));
  const fw = fortuneWeights(o.luck ?? (o.lucky ? g.kyoNextDayMult : 1));
  const fortunes: Fortune[] = [];
  for (let i = 0; i < count; i++) fortunes.push(weighted(h, fw));
  if (count >= 10 && !fortunes.some((f) => g.tenGuarantee.includes(f))) fortunes[count - 1] = weighted(h, fw.filter((x) => g.tenGuarantee.includes(x.item)));
  const kinds = o.dayKey ? pickupKinds(o.dayKey) : undefined;
  const pk = o.dayKey ? omikujiPickup(o.dayKey).defId : '';
  let pity = profile.gacha?.pity ?? 0;
  const items: GachaItem[] = [];
  const prizeW = PRIZES.map((k) => ({ item: k, weight: g.rates[k] ?? 0 }));
  for (let i = 0; i < count; i++) {
    let fortune = fortunes[i];
    const forced = pity + 1 >= g.pity;
    if (forced) fortune = 'daikichi';
    const kind: GachaPrize = forced ? 'omamori' : weighted(h, prizeW);
    const mult = fortune === 'daikichi' ? g.daikichiAmountMult : 1;
    switch (kind) {
      case 'omamori': {
        const om = rollOmamori(h, forced ? { rarity: 'ssr', kinds } : { rarityWeights: g.rarityByFortune[fortune] as Record<Rarity, number>, kinds });
        items.push({ kind, fortune, defId: om.defId, omamori: om, ...(om.defId === pk ? { pickup: true } : {}), ...(forced ? { pity: true } : {}), ...(g.omamoriZeni ? { zeni: g.omamoriZeni * mult } : {}) });
        break;
      }
      case 'equipment':
        items.push({ kind, fortune, drop: rollEquipDrop(h, tier, profile.oshi, [], { n: 0, ...g.equipRarity }) });
        break;
      case 'meibutsu': {
        const cands = MEIBUTSU.filter((m) => m.tier <= tier);
        const m = (cands.length ? cands : MEIBUTSU)[randInt(h, cands.length || MEIBUTSU.length)];
        items.push({ kind: 'equipment', fortune, meibutsu: true, drop: { defId: m.id, rarity: 'ssr', affixes: rollAffixes(h, EQUIPMENT[m.id].tier, 'ssr') } });
        break;
      }
      case 'fuda':
        items.push({ kind, fortune, amount: 1 * mult });
        break;
      case 'zaimoku':
        items.push({ kind, fortune, amount: g.zaimokuAmount * mult });
        break;
      case 'exp':
        items.push({ kind, fortune, amount: g.expAmount * mult });
        break;
      case 'zeni':
        items.push({ kind, fortune, amount: g.zeniAmount * mult });
        break;
    }
    const last = items[items.length - 1];
    pity = last.kind === 'omamori' && last.omamori.rarity === 'ssr' ? 0 : pity + 1;
  }
  return { items, pityAfter: pity, cost: gachaCost(count) };
}

function applyItems(p: Profile, res: Resources, items: GachaItem[], now: number, dayKey: string) {
  for (const it of items) {
    if (it.kind === 'omamori') {
      // 所持の上限を超えたら霊玉に（売値と同じ）
      const r = addOmamori(p, it.omamori);
      it.itemId = r.item?.id ?? null;
      res.zeni += it.zeni ?? 0;
      res.reidama += r.reidama;
    }
    else if (it.kind === 'equipment') {
      const r = addItem(p, it.drop);
      res.zeni += r.zeni;
      it.itemId = r.item?.id ?? null;
    } else if (it.kind === 'fuda') res.fuda += it.amount;
    else if (it.kind === 'zeni') res.zeni += it.amount;
    else if (it.kind === 'zaimoku') res.zaimoku += it.amount;
    else if (it.kind === 'exp') gainExp(p, res, it.amount, it.amount, now, dayKey);
  }
}

/** ガチャ券を払って引き、中身を反映する */
export function pullGacha(p: Profile, res: Resources, h: RngHolder, count: 1 | 10, now: number, dayKey: string): OpResult<{ items: GachaItem[]; cost: number }> {
  const cost = gachaCost(count);
  if (count !== 1 && count !== 10) return { ok: false, error: 'count' };
  if ((res.kuji ?? 0) < cost) return { ok: false, error: 'kuji' };
  const r = rollGacha(h, count, p, { dayKey, luck: omikujiPerk(p, dayKey).luck });
  res.kuji -= cost;
  p.gacha.pity = r.pityAfter;
  p.gacha.pulls += count;
  applyItems(p, res, r.items, now, dayKey);
  return { ok: true, items: r.items, cost };
}

/**
 * 課金の10連（ショップの「おまもりガチャ10連」・月パス）：買ったその場で引く。券としては残さない（前払式の残高を持たない）。
 * 券の10連と同じ抽選（ランクA以上1回確定・天井の回数・その日のボーナス）
 */
export function pullPaidGacha(p: Profile, res: Resources, h: RngHolder, now: number, dayKey: string): { items: GachaItem[] } {
  const r = rollGacha(h, 10, p, { dayKey, luck: omikujiPerk(p, dayKey).luck });
  p.gacha.pity = r.pityAfter;
  p.gacha.pulls += 10;
  applyItems(p, res, r.items, now, dayKey);
  return { items: r.items };
}

// ---------- 毎日の無料ガチャ ----------

export const freeOmikujiAvailable = (p: Profile, dayKey: string) => p.omikuji?.freeDay !== dayKey;

/** 無料の1回（券不要・JST の日付で1日1回）。そのランクでその日のボーナスが決まる */
export function pullFreeOmikuji(p: Profile, res: Resources, h: RngHolder, now: number, dayKey: string): OpResult<{ items: GachaItem[]; fortune: Fortune; perk: OmikujiPerk; lucky: boolean }> {
  if (!p.omikuji) p.omikuji = { freeDay: '', perk: null, luckDay: '', free: 0 };
  if (p.omikuji.freeDay === dayKey) return { ok: false, error: 'already' };
  const lucky = p.omikuji.luckDay === dayKey;
  const r = rollGacha(h, 1, p, { dayKey, lucky });
  p.gacha.pity = r.pityAfter;
  p.omikuji.freeDay = dayKey;
  p.omikuji.free = (p.omikuji.free ?? 0) + 1;
  const fortune = r.items[0].fortune;
  p.omikuji.perk = { day: dayKey, fortune };
  p.omikuji.luckDay = fortune === 'kyo' ? nextDay(dayKey) : '';
  applyItems(p, res, r.items, now, dayKey);
  return { ok: true, items: r.items, fortune, perk: omikujiPerk(p, dayKey), lucky };
}

/** その日のボーナス（無料ガチャのランク）。drop・zeni・zaimoku は報酬の倍率の上乗せ */
export interface OmikujiPerk {
  fortune: Fortune | null;
  drop: number;
  zeni: number;
  zaimoku: number;
  /** その日の券のガチャのランクS・A の重みの倍率（1 なら無し） */
  luck: number;
  /** 明日の無料ガチャで S・A が出やすい（D を引いた日）／今日がその日 */
  luckTomorrow: boolean;
  luckToday: boolean;
  text: string;
}

export function omikujiPerk(p: Profile, dayKey: string): OmikujiPerk {
  const perk = p.omikuji?.perk && p.omikuji.perk.day === dayKey ? p.omikuji.perk.fortune : null;
  const v = perk ? (CONFIG.gacha.perks[perk] ?? {}) : {};
  const luckTomorrow = perk === 'kyo' && p.omikuji.luckDay === nextDay(dayKey);
  const luckToday = !!p.omikuji?.luckDay && p.omikuji.luckDay === dayKey;
  return { fortune: perk, drop: v.drop ?? 0, zeni: v.zeni ?? 0, zaimoku: v.zaimoku ?? 0, luck: v.luck ?? 1, luckTomorrow, luckToday, text: perkText(perk) };
}

/** ボーナスの説明（無ければ空） */
export function perkText(f: Fortune | null): string {
  if (!f) return '';
  const g = CONFIG.gacha;
  if (f === 'kyo') return `明日の無料ガチャはランクS・Aが出やすい（抽選の重み×${g.kyoNextDayMult}）`;
  const v = g.perks[f] ?? {};
  const parts: string[] = [];
  if (v.drop) parts.push(`装備のドロップ+${Math.round(v.drop * 100)}%`);
  if (v.zeni) parts.push(`戦闘の銭+${Math.round(v.zeni * 100)}%`);
  if (v.zaimoku) parts.push(`戦闘の材木+${Math.round(v.zaimoku * 100)}%`);
  if (v.luck && v.luck > 1) parts.push(`ガチャ（券・ショップの10連・月パスの10連）でランクS・Aが出やすい（抽選の重み×${v.luck}）`);
  return parts.length ? `今日だけ ${parts.join('・')}` : '';
}
